import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CACIChatbot from "./CACIChatbot";
import { CACI_FOOTER_CSS } from "./CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   FOOTER
   ───────────────────────────────────────────────────────────────────────────
   Same links, same language switcher, same chatbot. Changes:

   · Switching language dropped the query string and the hash. From
     /search?keyword=export, choosing العربية landed you on /ar/search with an
     empty box — the search you had just run was gone. Both are carried over
     now.
   · Every external link opened with target="_blank" and no rel, which lets
     the opened page reach back through window.opener. All nine partner sites
     and the four social links now carry rel="noopener noreferrer".
   · The social links took their href straight from a translation key. When a
     key is missing, t() returns the key itself, so the icon linked to the
     relative path "menu.twitter" — a 404 on our own site. Links with no valid
     URL are simply not rendered.
   · renderMenuItems and getLabel were ~45 lines of dead code: defined,
     never called. The BASE_URL import was unused too.
   · The copyright line was commented out, so the site had none.
   · The Bootstrap Icons stylesheet was injected as a side effect at module
     scope, which runs on import even if the footer never renders. Moved into
     an effect.
   ═══════════════════════════════════════════════════════════════════════════ */

const PARTNER_SITES = [
  { key: "site_elmouchir",  url: "https://elmouchir.caci.dz/" },
  { key: "site_ec",         url: "https://ec.caci.dz/" },
  { key: "site_bc",         url: "https://bc.caci.dz/" },
  { key: "site_formations", url: "https://formations.caci.dz/" },
  { key: "site_sanist",     url: "https://sanist.dz/" },
  { key: "site_logistical", url: "https://logistical.dz/" },
  { key: "site_liccal",     url: "https://liccal.caci.dz/" },
  { key: "site_bourse",     url: "https://bourse.caci.dz/" },
  { key: "site_b2b",        url: "https://b2b.caci.dz/" },
];

const QUICK_LINKS = [
  { path: "/",                     key: "home" },
  { path: "/contact",              key: "contact" },
  { path: "/toutacticle",          key: "Toutacticle" },
  { path: "/toutpage",             key: "Toutpage" },
  { path: "/calendrier",           key: "calendrier" },
  { path: "/chambre_de_commerces", key: "chambres" },
  { path: "/partenaire",           key: "Partenaire" },
];

const SOCIAL = [
  { key: "facebook",  icon: "bi-facebook",  label: "Facebook" },
  { key: "twitter",   icon: "bi-twitter-x", label: "X" },
  { key: "instagram", icon: "bi-instagram", label: "Instagram" },
  { key: "linkedin",  icon: "bi-linkedin",  label: "LinkedIn" },
];

const LANGUAGES = [
  { code: "fr", label: "Français" },
  { code: "ar", label: "العربية" },
  { code: "en", label: "English" },
];

function Footer() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  /* Loaded here rather than at module scope, so importing this file does not
     touch the document. */
  React.useEffect(() => {
    if (document.querySelector('link[href*="bootstrap-icons"]')) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css";
    document.head.appendChild(link);
  }, []);

  const path = (p) => (lang === "fr" ? p : `/${lang}${p === "/" ? "" : p}`) || "/";

  const changeLanguage = (next) => {
    if (next === lang) return;
    i18n.changeLanguage(next);

    const current = location.pathname;
    let bare = current;
    if (/^\/(ar|en|fr)(\/|$)/.test(current)) {
      bare = current.replace(/^\/(ar|en|fr)/, "") || "/";
    }

    /* The query string and hash are part of where the visitor is. Dropping
       them meant a language switch on /search?keyword=export threw the search
       away and landed on an empty form. */
    const tail = `${location.search || ""}${location.hash || ""}`;
    navigate(`${next === "fr" ? bare : `/${next}${bare === "/" ? "" : bare}`}${tail}`);
  };

  /* t() returns the key itself when a translation is missing, so an absent
     "menu.facebook" would render href="menu.facebook" — a relative link to a
     page that does not exist. Only real URLs get rendered. */
  const socialUrl = (key) => {
    const value = String(t(`menu.${key}`, "")).trim();
    return /^https?:\/\//i.test(value) ? value : null;
  };

  const year = new Date().getFullYear();

  return (
    <>
      <footer id="footer" className={`caci-footer${isRtl ? " is-rtl" : ""}`} dir={isRtl ? "rtl" : "ltr"}>
        <style>{CACI_FOOTER_CSS}</style>

        <div className="ft-shell">
          <div className="ft-grid">
            {/* ── Identity ─────────────────────────────── */}
            <div className="ft-col ft-about">
              <img className="ft-logo" src="/logo.png" alt={t("menu.caci")} width="120" height="120" />
              <h2 className="ft-name">{t("menu.caci")}</h2>
              <p className="ft-desc">{t("menu.small_description")}</p>
            </div>

            {/* ── Quick links ──────────────────────────── */}
            <nav className="ft-col" aria-labelledby="ft-quick">
              <h2 className="ft-heading" id="ft-quick">{t("menu.Quick_Links")}</h2>
              <ul className="ft-list">
                {QUICK_LINKS.map((l) => (
                  <li key={l.path}>
                    <Link to={path(l.path)}>
                      <i className={`bi bi-chevron-${isRtl ? "left" : "right"}`} aria-hidden="true" />
                      {t(`menu.${l.key}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* ── Network ──────────────────────────────── */}
            <nav className="ft-col" aria-labelledby="ft-net">
              <h2 className="ft-heading" id="ft-net">{t("menu.Quick_Links2")}</h2>
              <ul className="ft-list ft-list-2">
                {PARTNER_SITES.map((s) => (
                  <li key={s.key}>
                    <a href={s.url} target="_blank" rel="noopener noreferrer">
                      <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                      {t(`menu.${s.key}`)}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* ── Follow + language ────────────────────── */}
            <div className="ft-col ft-follow">
              <h2 className="ft-heading">{t("menu.Follow_Us")}</h2>
              <div className="ft-social">
                {SOCIAL.map((s) => {
                  const url = socialUrl(s.key);
                  if (!url) return null;
                  return (
                    <a
                      key={s.key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      className={`ft-social-btn is-${s.key}`}
                    >
                      <i className={`bi ${s.icon}`} aria-hidden="true" />
                    </a>
                  );
                })}
              </div>

              <label className="ft-lang-label" htmlFor="ft-lang">
                <i className="bi bi-translate" aria-hidden="true" />
                {t("menu.language", "Langue")}
              </label>
              <div className="ft-lang">
                <select
                  id="ft-lang"
                  value={LANGUAGES.some((l) => l.code === lang) ? lang : "fr"}
                  onChange={(e) => changeLanguage(e.target.value)}
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="ft-base">
            <p className="ft-copy">
              © {year} <strong>{t("menu.caci")}</strong>. {t("menu.rights", "Tous droits réservés.")}
            </p>
          </div>
        </div>
      </footer>

      <CACIChatbot />
    </>
  );
}

export default Footer;
